import IssueApiRequest from '@/api/issues'
import { JobsFilterType, JobType, StatusCodeType } from '@/enum'
import {
  DeleteTypeExpression,
  ItemTypeExpression,
  ListFilterTypeExpression,
  ListResponseTypeExpression
} from '@/enum/LinqExpressionType'
import NotificationType from '@/enum/NotificationType'
import { Filter, Job, JobUpdate } from '@/models'
import IssueJobForm from '@/models/jobs/issues/IssueJobForm'
import ErrorHandlerService from '@/services/ErrorHandlerService'
import {
  ISSUES_LIST_STORE,
  JOB_DETAILS_EXPAND_PARAMS,
  JOBS_LIST_EXPAND_PARAMS,
  MESSAGE_CONSTANTS,
  Network
} from '@/utils/constants'
import CONSTANTS from '@/utils/constants/string-constants'
import { pluralize } from '@/utils/helpers'
import { defineStore } from 'pinia'
import useGlobalStore from '../global'
import usePaginationStore from '../pagination'
import state from './state'
import type { IExplicitlyUpdatedJobProperty } from '@/interfaces'
import ErrorType from '@/enum/ErrorType'

const useIssuesStore = defineStore('issues', {
  state: () => state,
  actions: {
    async getIssues(
      storeName: string,
      responseOptions?: any,
      ListItemClass?: new (item: any) => any,
      isMinimal = false
    ) {
      const issuesStore = usePaginationStore(storeName)
      const issuesApi = new IssueApiRequest()
      if (!isMinimal) {
        responseOptions = {
          ...responseOptions,
          [ListResponseTypeExpression.Expand]: JOBS_LIST_EXPAND_PARAMS
        }
      }

      return issuesStore.handleListAPICall<Job>(
        issuesApi.getIssues.bind(issuesApi),
        ListItemClass ?? Job,
        responseOptions
      )
    },
    async getIssuesForDropdown(storeName: string) {
      const responseOptions = {
        [ListResponseTypeExpression.Select]: 'Title, Id',
        [ListFilterTypeExpression.Filter]: {
          [JobsFilterType.TYPE]: JobType.ISSUE
        }
      }
      const result = await this.getIssues(storeName, responseOptions, Filter, true)
      return result
    },
    async createIssue() {
      const issuesApi = new IssueApiRequest()
      const newIssue = new JobUpdate(this.issueForm)
      return await issuesApi.createIssue(newIssue)
    },
    async getIssue(id: string) {
      this.isIssueFormLoading = true

      const issuesApi = new IssueApiRequest(false, true)
      try {
        const result = await issuesApi.getIssue({
          id,
          [ItemTypeExpression.Expand]: JOB_DETAILS_EXPAND_PARAMS
        })
        if (result.status === StatusCodeType.Success && result.data) {
          this.issueForm = new IssueJobForm(result.data)
        }
        return result
      } catch (error: any) {
        const globalStore = useGlobalStore()
        if ([StatusCodeType.NotFound, StatusCodeType.Invalid].includes(error.status)) {
          globalStore.addError(
            {
              name: MESSAGE_CONSTANTS.ISSUE_WAS_DELETED_ALERT_MESSAGE,
              title: MESSAGE_CONSTANTS.ISSUE_WAS_DELETED_ALERT_TITLE
            },
            ErrorType.Network
          )
        }
        return error
      } finally {
        this.isIssueFormLoading = false
      }
    },
    async updateIssue(id: string, explicitlyUpdatedIssueProperty?: IExplicitlyUpdatedJobProperty) {
      const issuesApi = new IssueApiRequest(false, true)
      const updatedIssue = new JobUpdate(explicitlyUpdatedIssueProperty ?? this.issueForm)
      try {
        const result = await issuesApi.updateIssue(
          {
            id,
            [ItemTypeExpression.Expand]: JOB_DETAILS_EXPAND_PARAMS
          },
          updatedIssue
        )
        return result
      } catch (error: any) {
        const title = error?.response.data?.detail ?? MESSAGE_CONSTANTS.ISSUE_WAS_DELETED_ALERT_TITLE

        const globalStore = useGlobalStore()
        if ([StatusCodeType.NotFound, StatusCodeType.Invalid].includes(error.status)) {
          globalStore.addError(
            {
              title
            },
            ErrorType.Network
          )
        }
        return error
      }
    },
    async deleteIssues() {
      const issuesApi = new IssueApiRequest()
      const globalStore = useGlobalStore()
      const issuesListStore = usePaginationStore(ISSUES_LIST_STORE)

      try {
        let queryParams
        const issuesToDeleteIds = issuesListStore.selectedItems ?? []

        if (!issuesToDeleteIds.length) throw new Error('No issues to delete')

        if (
          issuesListStore.someItemsSelected ||
          (issuesListStore.allItemsSelected && !issuesListStore.isSelectedAllSnackbarItems)
        )
          queryParams = { [DeleteTypeExpression.Expression]: { Id: issuesToDeleteIds } }

        const response = await issuesListStore.handleListDeleteAPICall(
          issuesApi.deleteIssues.bind(issuesApi),
          queryParams
        )

        if (
          response?.status === StatusCodeType.Success &&
          response.data.succeeded > 0 &&
          response.data.failed === 0
        ) {
          globalStore.addToastNotification({
            id: Symbol(`Issued deleted`),
            message: `${pluralize(response.data.succeeded, CONSTANTS.ISSUES)} ${CONSTANTS.DELETED_SUCCESSFULLY}`,
            type: NotificationType.Success
          })

          return Promise.resolve(response.data)
        }

        if (response?.data && response.data.failed > 0) {
          globalStore.handleBulkOperationErrorsView(response, CONSTANTS.ISSUES)
          return Promise.resolve(response.data)
        }

        if (response?.data?.results.length) return Promise.reject(response.data)
        throw new TypeError(Network.TypeError.BulkOperationTypeMismatch)
      } catch (error: any) {
        ErrorHandlerService.handleAndCatchError(error, error)
      }

      return Promise.reject()
    },
    resetIssueForm() {
      this.issueForm = new IssueJobForm()
    }
  }
})

export default useIssuesStore
